import {useTranslation} from 'react-i18next';
import {UserPlusIcon, UsersIcon} from 'lucide-react';
import {Button} from '@chekin/guestapp-ui-kit';
import checkedCircleIcon from 'assets/icons/checkedCircle.svg';
import questionAlertCircleIcon from 'assets/icons/questionAlertCircle.svg';
import ModalSheet from 'components/common/ModalSheet';
import {ProgressTaskBar} from '../ProgressTaskBar';
import {ShareRegistrationLinkButton} from 'components/ShareRegistrationLinkButton';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registeredGuests?: number;
  pendingGuests?: number;
  onRegisterNext?: () => void;
  onGoHome?: () => void;
}

function RegisteredGuestsInfoModal({
  isOpen,
  onClose,
  registeredGuests = 1,
  pendingGuests = 3,
  onRegisterNext,
  onGoHome,
}: Props) {
  const {t} = useTranslation();
  const totalGuests = registeredGuests + pendingGuests;

  return (
    <ModalSheet
      isOpenModalSheet={isOpen}
      handleCloseModal={onClose}
      withCloseButton={Boolean(registeredGuests)}
      closeOnDocumentClick={false}
      closeButtonType="slide"
      closeOnEscape={false}
      className="[&>div]:py-[40px] [&>div]:px-6 kiosko:rounded-[38px]"
    >
      <div className="flex flex-col gap-14 p-0 w-full">
        <ProgressTaskBar
          completed={registeredGuests}
          total={totalGuests}
          title={t('registered_guests')}
          icon={<UsersIcon size={48} color="#777E91" />}
        />

        <div className="flex flex-col p-8 rounded-[38px] bg-[#f4f6f9] border border-[#e9ecef] gap-9 mb-[46px]">
          <StatusItem
            numberOfGuests={registeredGuests}
            text={t('registered_successfully')}
            icon={checkedCircleIcon}
          />
          <StatusItem
            numberOfGuests={pendingGuests}
            text={t('pending_registration')}
            icon={questionAlertCircleIcon}
          />
        </div>

        <div className="flex flex-col gap-4 kiosko:gap-9">
          <Button onClick={onRegisterNext} size="md" className="m-auto gap-[18px]">
            <UserPlusIcon color="#fff" className="w-14 h-14" />
            {t('register_next_guest')}
          </Button>
          <Button
            onClick={onGoHome}
            size="md"
            className="m-auto gap-[18px]"
            variant="outline"
          >
            {t('go_to_home')}
          </Button>

          <ShareRegistrationLinkButton />
        </div>
      </div>
    </ModalSheet>
  );
}

interface StatusItemProps {
  numberOfGuests: number;
  text: string;
  icon: string;
}

function StatusItem({numberOfGuests, text, icon}: StatusItemProps) {
  const {t} = useTranslation();
  const plural = numberOfGuests !== 1 ? t('guests') : t('guest');
  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center justify-center shrink-0">
        <img src={icon} alt="pending" className="w-14 h-14" />
      </div>
      <span className="text-kiosko-s font-medium text-[#374151]">
        {numberOfGuests} <span>{plural}</span> {text}
      </span>
    </div>
  );
}

export {RegisteredGuestsInfoModal};
